import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ShoppingCart, Heart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [customization, setCustomization] = useState({});
  const { addToCart, toggleFavorite, isFavorite } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/products/${productId}`);
      setProduct(data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Producto no encontrado');
      navigate('/catalogo');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    const result = await addToCart(productId, quantity, customization, product);
    if (result.success) {
      toast.success('Producto añadido al carrito');
    } else {
      toast.error('Error al añadir al carrito');
    }
  };

  const handleToggleFavorite = async () => {
    await toggleFavorite(productId);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#73706e]">Cargando producto...</p>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="sticky top-24 h-fit">
            <div className="rounded-xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
              <img
                src={product.images && product.images[0] ? product.images[0] : 'https://via.placeholder.com/600x600?text=Producto'}
                alt={product.name}
                className="w-full h-auto"
                data-testid="product-detail-image"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-light text-[#374963] mb-4" data-testid="product-detail-name">
                {product.name}
              </h1>
              <p className="text-3xl font-semibold text-[#785534]" data-testid="product-detail-price">
                {product.price.toFixed(2)}€
              </p>
            </div>

            <div className="border-t border-[rgba(55,73,99,0.1)] pt-6">
              <h2 className="text-xl font-semibold text-[#374963] mb-2">Descripción</h2>
              <p className="text-[#73706e] leading-relaxed" data-testid="product-detail-description">
                {product.description}
              </p>
            </div>

            <div className="border-t border-[rgba(55,73,99,0.1)] pt-6">
              <h2 className="text-xl font-semibold text-[#374963] mb-4">Cantidad</h2>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-2 bg-[#374963] text-white rounded-lg hover:bg-[#4A5F7D] transition-colors"
                  data-testid="quantity-decrease-button"
                >
                  -
                </button>
                <span className="text-2xl font-semibold text-[#374963]" data-testid="quantity-display">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-2 bg-[#374963] text-white rounded-lg hover:bg-[#4A5F7D] transition-colors"
                  data-testid="quantity-increase-button"
                >
                  +
                </button>
              </div>
            </div>

            <div className="border-t border-[rgba(55,73,99,0.1)] pt-6 space-y-4">
              <button
                onClick={handleAddToCart}
                className="w-full py-4 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors flex items-center justify-center space-x-2"
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="w-5 h-5" />
                <span>Añadir al Carrito</span>
              </button>

              <button
                onClick={handleToggleFavorite}
                className="w-full py-4 bg-white border border-[rgba(55,73,99,0.2)] text-[#374963] rounded-lg hover:bg-[#FAFAFA] transition-colors flex items-center justify-center space-x-2"
                data-testid="toggle-favorite-button"
              >
                <Heart
                  className={`w-5 h-5 ${
                    isFavorite(productId) ? 'fill-[#785534] text-[#785534]' : 'text-[#374963]'
                  }`}
                />
                <span>{isFavorite(productId) ? 'Quitar de Favoritos' : 'Añadir a Favoritos'}</span>
              </button>
            </div>

            <div className="bg-[#FAFAFA] p-6 rounded-lg">
              <p className="text-[#73706e] text-sm">
                <strong>Nota:</strong> Este producto se fabrica bajo pedido. El tiempo de producción es de 3-5 días laborables.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetailPage;